import React, { useCallback, useEffect, useRef, useState } from "react";
import useSongStore from "../../store/SongState";
import { areSameSongs, getQueryParams, parseTime } from "../../../../../utils/utils";
import { ProgressSliderApi } from "../../../../shared/progress-slider/view";
import { Song } from "../../types";
import LocalStorage, { CURR_SONG_KEY, CURR_TIME_KEY, SONG_QUEUE_KEY } from "../../../../../utils/local-storage";
import { fetchSongAudioError, getCachedResolvedTrack, getCanvasVideoUrl, getSongArtworkUrlSync, getSongAudioUrl, getSongAudioUrlSync, getSongImageUrl, isSpotip17anNativeSong, normalizeFileUrl, resolveSongTrack, Spotip17anFunctions } from "../../../../../utils/api";
import toast from "react-hot-toast";
import { DiscordBotApi } from "../../../../../utils/discordBotApi";
import useUserStore from "../../../../../store/UserState";

type UseAudioPlayerProps = {
    audioRef: React.RefObject<HTMLAudioElement>;
    controlsRef: React.RefObject<HTMLDivElement>;
    progressSlider: React.RefObject<ProgressSliderApi>;
    maximizeSongPlayer: () => void;
    isFullScreen?: boolean;
}

function safeSetMediaSessionPositionState(state: { duration?: number; position?: number; playbackRate?: number }) {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator) || typeof navigator.mediaSession?.setPositionState !== 'function') {
        return;
    }
    const duration = state.duration;
    if (typeof duration !== 'number' || !Number.isFinite(duration) || duration <= 0) {
        return;
    }

    const position = state.position;
    const safePosition = typeof position === 'number' && Number.isFinite(position) && position >= 0
        ? Math.min(position, duration)
        : 0;

    const playbackRate = state.playbackRate;
    const safePlaybackRate = typeof playbackRate === 'number' && Number.isFinite(playbackRate) && playbackRate > 0
        ? playbackRate
        : 1;

    try {
        navigator.mediaSession.setPositionState({
            duration,
            playbackRate: safePlaybackRate,
            position: safePosition
        });
    } catch {
        // Silently ignore browser mediaSession issues to prevent app crash
    }
}

function buildArtwork(url?: string) {
    const defaultIcon = typeof window !== 'undefined' ? `${window.location.origin}/logo512.png` : 'https://spotip17an.es/logo512.png';
    let validUrl = url;
    if (!validUrl || validUrl.startsWith('blob:') || validUrl.startsWith('data:')) {
        validUrl = defaultIcon;
    } else if (!validUrl.startsWith('http://') && !validUrl.startsWith('https://')) {
        validUrl = normalizeFileUrl(validUrl);
        if (!validUrl.startsWith('http')) {
            validUrl = defaultIcon;
        }
    }
    return [
        { src: validUrl, sizes: '96x96' },
        { src: validUrl, sizes: '128x128' },
        { src: validUrl, sizes: '192x192' },
        { src: validUrl, sizes: '256x256' },
        { src: validUrl, sizes: '384x384' },
        { src: validUrl, sizes: '512x512' }
    ];
}

const useAudioPlayer = ({ audioRef, controlsRef, progressSlider, maximizeSongPlayer, isFullScreen }: UseAudioPlayerProps) => {

    const [paused, setPaused] = React.useState(true);
    const { setCurrentSong, state, playNextSong, setQueue, emptyHistory, syncWithBotState, setBotMode } = useSongStore(state => state);
    const { currentSong, loopMode, randomQueue, isBotMode, botGuildId, botVoiceChannelId, botPlaying, botPlaybackSeconds, botDuration } = state;
    const discordUser = useUserStore(state => state.state.discordUser);
    const audioPlayer = audioRef.current;
    const timeSlider = progressSlider.current;
    const [currentTime, setCurrentTime] = useState("00:00");
    const [duration, setDuration] = useState("00:00");
    const [botLocalTime, setBotLocalTime] = useState(0);
    const playingSong = useRef<Song>();
    const isPlayingIntent = useRef(false);
    const preloadedSongId = useRef<string | number | null>(null);
    const timeUpdateThrottle = useRef<any>(null);
    const saveTimeThrottle = useRef<any>(null);
    const errorHandledSongId = useRef<string | number | null>(null);
    const errorTimeoutRef = useRef<any>(null);
    const isTransitioningTrack = useRef(false);

    useEffect(() => {
        return () => {
            if (errorTimeoutRef.current) {
                clearTimeout(errorTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (playingSong.current && (playingSong.current.id > 0 || playingSong.current.id.length > 4)) {
            LocalStorage.setItem(CURR_SONG_KEY, playingSong.current);
        }
    }, [playingSong.current]);

    const wasBotMode = useRef(isBotMode);

    useEffect(() => {
        if (!audioPlayer) return;

        if (isBotMode) {
            isPlayingIntent.current = false;
            audioPlayer.pause();
            wasBotMode.current = true;
            return;
        }

        const forceReload = wasBotMode.current;
        wasBotMode.current = false;

        const isSameSong = !!(currentSong && playingSong.current && areSameSongs(currentSong, playingSong.current));
        if (!forceReload && isSameSong) {
            // Already loaded / playing this exact song: do NOT restart or pause!
            const currentAudioUrl = getSongAudioUrlSync(currentSong!);
            const playingAudioUrl = getSongAudioUrlSync(playingSong.current!);
            if (currentAudioUrl === playingAudioUrl) {
                return;
            }
        }

        let songQueue = null;
        if (!currentSong) {
            isPlayingIntent.current = false;
            audioPlayer.pause();

            const idLoaded: string | undefined = getQueryParams(window.location.href)['song'];
            if (idLoaded) {
                Spotip17anFunctions.use('fetchSongById').call({ songId: idLoaded }).then((res) => {
                    playSong(res, false, 0);
                    setCurrentSong(res);
                    maximizeSongPlayer();
                });
                return;
            }

            const loadSong = LocalStorage.getItem<Song>(CURR_SONG_KEY);
            if (loadSong) {
                const time = LocalStorage.getItem<number>(CURR_TIME_KEY);
                playSong(loadSong, false, time ?? 0);
                setCurrentSong(loadSong);
            }

            songQueue = LocalStorage.getItem<Song[]>(SONG_QUEUE_KEY);
            if (songQueue?.length) {
                setQueue(songQueue);
            }
        } else {
            const shouldPlay = isPlayingIntent.current || !isSameSong;
            playSong(currentSong, shouldPlay);
        }

    }, [currentSong, audioPlayer, isBotMode]);

    // Polling of bot state
    useEffect(() => {
        if (!isBotMode || !botGuildId) return;

        let active = true;
        const pollBotState = async () => {
            try {
                const botState = await DiscordBotApi.getCurrentState(botGuildId);
                if (active && botState && botState.success && botState.state) {
                    const s = botState.state;

                    if (s.connected === false) {
                        if (discordUser?.id) {
                            try {
                                const voiceStatus = await DiscordBotApi.getUserVoiceStatus(discordUser.id);
                                const currentGuildId = s.guildId || botGuildId;
                                if (!voiceStatus.guildId || voiceStatus.guildId !== currentGuildId) {
                                    setBotMode(false);
                                    return;
                                }
                            } catch (voiceErr) {
                                console.error("Error checking user voice status on bot disconnected:", voiceErr);
                            }
                        } else {
                            setBotMode(false);
                            return;
                        }
                    }

                    const dur = s.currentSong ? s.currentSong.duration : 0;
                    syncWithBotState(
                        s.currentSong,
                        s.songQueue || [],
                        s.history || [],
                        s.loopMode || 'none',
                        s.playing,
                        s.playbackSeconds || 0,
                        dur
                    );
                }
            } catch (err) {
                console.error("Error polling bot state:", err);
            }
        };

        const intervalMs = 1000;
        pollBotState();
        const interval = setInterval(pollBotState, intervalMs);

        return () => {
            active = false;
            clearInterval(interval);
        };
    }, [isBotMode, botGuildId, syncWithBotState, discordUser, setBotMode]);

    // Periodic check (every 5 seconds) to see if user has changed voice channel or left
    useEffect(() => {
        if (!isBotMode || !discordUser?.id) return;

        let active = true;

        const checkUserVoiceStatus = async () => {
            try {
                const voiceStatus = await DiscordBotApi.getUserVoiceStatus(discordUser.id);
                if (!active) return;

                if (!voiceStatus.success || !voiceStatus.guildId || !voiceStatus.voiceChannelId) {
                    setBotMode(false);
                    return;
                }

                if (voiceStatus.guildId !== botGuildId || voiceStatus.voiceChannelId !== botVoiceChannelId) {
                    const botState = await DiscordBotApi.getCurrentState(voiceStatus.guildId);
                    if (!active) return;

                    if (botState && botState.success && botState.state) {
                        const s = botState.state;
                        const isBotConnected = s.connected;
                        const botChannelId = s.voiceChannelId || s.channelId;

                        if (isBotConnected && botChannelId === voiceStatus.voiceChannelId) {
                            setBotMode(true, voiceStatus.guildId, voiceStatus.voiceChannelId);
                        } else {
                            setBotMode(false);
                        }
                    } else {
                        setBotMode(false);
                    }
                }
            } catch (err) {
                console.error("Error checking voice status periodically:", err);
            }
        };

        const interval = setInterval(checkUserVoiceStatus, 5000);
        return () => {
            active = false;
            clearInterval(interval);
        };
    }, [isBotMode, discordUser?.id, botGuildId, botVoiceChannelId, setBotMode]);

    // Sync botLocalTime with store botPlaybackSeconds when polling updates it
    useEffect(() => {
        if (isBotMode) {
            setBotLocalTime(botPlaybackSeconds);
        }
    }, [isBotMode, botPlaybackSeconds]);

    // Local real-time ticker to increment botLocalTime smoothly when playing
    useEffect(() => {
        if (!isBotMode || !botPlaying) return;

        const startTime = Date.now();
        const startPlaybackSeconds = botPlaybackSeconds;

        const interval = setInterval(() => {
            const deltaSeconds = (Date.now() - startTime) / 1000;
            const estimatedTime = startPlaybackSeconds + deltaSeconds;
            const finalTime = Math.min(estimatedTime, botDuration);
            setBotLocalTime(finalTime);
        }, 100);

        return () => clearInterval(interval);
    }, [isBotMode, botPlaying, botPlaybackSeconds, botDuration]);

    // Sync UI controls/progress state with Bot Mode
    useEffect(() => {
        if (isBotMode) {
            setPaused(!botPlaying);
            setCurrentTime(parseTime(botLocalTime));
            setDuration(parseTime(botDuration));
            if (progressSlider.current) {
                progressSlider.current.setProgress(botLocalTime * 1000);
                progressSlider.current.setMax(botDuration * 1000);
            }
        }
    }, [isBotMode, botPlaying, botLocalTime, botDuration, progressSlider]);

    const preloadNextTrackInBackground = useCallback(() => {
        const nextSong = state.songQueue?.[0];
        if (!nextSong || preloadedSongId.current === nextSong.id) return;
        preloadedSongId.current = nextSong.id;

        // 1. Warm up backend cache if YouTube song
        if (!isSpotip17anNativeSong(nextSong)) {
            Spotip17anFunctions.use('fetchYoutubeSong').post({ id: nextSong.id, onlyLoad: "true" })
                .catch(() => {});
        }
        // 2. Pre-resolve metadata, artwork, and audio in memory
        resolveSongTrack(nextSong);
        getSongImageUrl(nextSong);
        getSongAudioUrl(nextSong);
        getCanvasVideoUrl(nextSong);
    }, [state.songQueue]);

    useEffect(() => {
        if (!state.songQueue?.length) return;
        preloadNextTrackInBackground();
    }, [state.songQueue, preloadNextTrackInBackground]);

    const handleSongError = async (failedSong?: Song) => {
        const songToHandle = failedSong || playingSong.current || currentSong;
        if (!songToHandle) return;

        // Check if audio has a valid non-empty source
        const currentSrc = audioPlayer?.currentSrc || audioPlayer?.src;
        if (!currentSrc || currentSrc === "" || currentSrc === window.location.href) {
            return;
        }

        // Prevent double execution for the same song
        if (errorHandledSongId.current === songToHandle.id) return;

        console.error("Playback error event for song:", songToHandle.name);

        const errorDetails = await fetchSongAudioError(songToHandle);

        // If the backend did NOT return an error (it was 200 OK or no error details), ignore the false alarm!
        if (!errorDetails) {
            console.log("No backend error found for song, ignoring false alarm error event.");
            return;
        }

        errorHandledSongId.current = songToHandle.id;

        let errorMessage = `No se pudo reproducir "${songToHandle.name}".`;

        switch (errorDetails.code) {
            case "VIDEO_NOT_FOUND":
                errorMessage = `"${songToHandle.name}" no está disponible o ha sido eliminado de YouTube.`;
                break;
            case "VIDEO_PRIVATE":
                errorMessage = `"${songToHandle.name}" es un video privado o requiere permisos de acceso.`;
                break;
            case "VIDEO_COPYRIGHT_RESTRICTED":
                errorMessage = `"${songToHandle.name}" está bloqueado por derechos de autor.`;
                break;
            case "VIDEO_AGE_RESTRICTED":
                errorMessage = `"${songToHandle.name}" tiene restricción de edad.`;
                break;
            case "INVALID_URL":
            case "INVALID_VIDEO_ID":
                errorMessage = `URL o ID no válido para "${songToHandle.name}".`;
                break;
            case "DOWNLOAD_ERROR_YTDL":
            case "DOWNLOAD_ERROR_INNERTUBE":
                errorMessage = `Error del servidor al procesar el audio de "${songToHandle.name}".`;
                break;
            case "NETWORK_ERROR":
                errorMessage = `Error de conexión al cargar "${songToHandle.name}".`;
                break;
            default:
                errorMessage = errorDetails.message || errorMessage;
                break;
        }

        toast.error(errorMessage, { duration: 4000 });
    };

    const playSong = async (song: Song, play?: boolean, time?: number) => {
        if (!audioPlayer) return;

        if (play !== undefined) {
            isPlayingIntent.current = play;
        }

        const immediateAudioUrl = getSongAudioUrlSync(song);

        // If already playing this song with the same audio source:
        if (areSameSongs(song, playingSong.current) && audioPlayer.src === immediateAudioUrl) {
            if (play && audioPlayer.paused) {
                audioPlayer.play().catch(() => {});
            }
            return;
        }

        console.log("Playing song: ", song.name, play, time, playingSong.current);

        if (errorTimeoutRef.current) {
            clearTimeout(errorTimeoutRef.current);
            errorTimeoutRef.current = null;
        }
        errorHandledSongId.current = null;

        playingSong.current = song;

        const startTime = time ?? 0;
        updateSongPlayerTime(startTime, song.duration, true, true);
        audioPlayer.currentTime = startTime;

        // 1. Immediately and synchronously update MediaSession before any async operation
        updateMediaSesion(song, audioPlayer, startTime);

        if (immediateAudioUrl && immediateAudioUrl.length > 0) {
            // 2. Set audio source immediately and synchronously
            if (audioPlayer.src !== immediateAudioUrl) {
                audioPlayer.src = immediateAudioUrl;
                audioPlayer.load();
            }
            console.log("Audio source: ", immediateAudioUrl);

            if (play) {
                if ('mediaSession' in navigator) {
                    navigator.mediaSession.playbackState = 'playing';
                }
                const playPromise = audioPlayer.play();
                if (playPromise !== undefined) {
                    playPromise.catch((e) => {
                        if (e?.name === 'AbortError') {
                            return;
                        }
                        console.error('Error playing song', e);
                        if (e?.name !== 'NotAllowedError') {
                            isPlayingIntent.current = false;
                            if ('mediaSession' in navigator) {
                                navigator.mediaSession.playbackState = 'paused';
                            }
                            handleSongError(song);
                        }
                    });
                }
            } else {
                audioPlayer.pause();
            }

            // 3. In background, check if offline IndexedDB version exists (only swap if not already playing smoothly)
            getSongAudioUrl(song).then((dbAudioUrl) => {
                if (dbAudioUrl && dbAudioUrl !== immediateAudioUrl && playingSong.current?.id === song.id) {
                    if (audioPlayer.src !== dbAudioUrl && audioPlayer.currentTime === 0 && audioPlayer.paused) {
                        audioPlayer.src = dbAudioUrl;
                        if (play) {
                            audioPlayer.play().catch(() => { });
                        }
                    }
                }
            }).catch(() => { });
        }

        // Trigger preloading of next song in background
        preloadNextTrackInBackground();
    };

    const updateMediaSesion = (song: Song, audioPlayer: HTMLAudioElement, initialTime: number = 0) => {
        if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;

        try {
            // Check if already pre-resolved in queue
            const cachedResolved = getCachedResolvedTrack(song);
            const title = cachedResolved?.title || song.name;
            const artist = (cachedResolved?.artists && cachedResolved.artists.length > 0)
                ? cachedResolved.artists.map(a => a.name).join(', ')
                : (cachedResolved?.artist || song.author_name || song.user?.username || 'Artista desconocido');

            const posterUrl = getSongArtworkUrlSync(song);
            const initialArtwork = buildArtwork(posterUrl);

            // 1. Immediately and synchronously update metadata to keep OS notification alive
            navigator.mediaSession.metadata = new MediaMetadata({
                title: title,
                artist: artist,
                album: 'Spotip17an',
                artwork: initialArtwork
            });

            navigator.mediaSession.playbackState = isPlayingIntent.current ? 'playing' : 'paused';

            safeSetMediaSessionPositionState({
                duration: song.duration || 1,
                playbackRate: 1,
                position: initialTime
            });

            const setAction = (action: MediaSessionAction, handler: MediaSessionActionHandler) => {
                try {
                    navigator.mediaSession.setActionHandler(action, handler);
                } catch { }
            };

            setAction('play', () => {
                isPlayingIntent.current = true;
                if ('mediaSession' in navigator) {
                    navigator.mediaSession.playbackState = 'playing';
                }
                audioPlayer.play();
            });
            setAction('pause', () => {
                isPlayingIntent.current = false;
                if ('mediaSession' in navigator) {
                    navigator.mediaSession.playbackState = 'paused';
                }
                audioPlayer.pause();
            });
            setAction('seekbackward', (event) => {
                audioPlayer.currentTime = Math.max(audioPlayer.currentTime - (event.seekOffset ?? 10), 0);
            });
            setAction('seekforward', (event) => {
                audioPlayer.currentTime = Math.min(audioPlayer.currentTime + (event.seekOffset ?? 10), audioPlayer.duration);
            });
            setAction('previoustrack', () => prevTrack());
            setAction('nexttrack', () => nextTrack());
            setAction('seekto', (details) => {
                if (details.seekTime !== undefined && details.seekTime !== null && !isNaN(details.seekTime)) {
                    audioPlayer.currentTime = details.seekTime;
                    updateSongPlayerTime(audioPlayer.currentTime, audioPlayer.duration);
                }
            });

            // 2. If not yet resolved, enrich metadata asynchronously
            if (!cachedResolved) {
                (async () => {
                    try {
                        const resolved = await resolveSongTrack(song);
                        if (playingSong.current?.id !== song.id && state.currentSong?.id !== song.id) return;

                        const enrichedArtist = (resolved?.artists && resolved.artists.length > 0)
                            ? resolved.artists.map(a => a.name).join(', ')
                            : (resolved?.artist || artist);
                        const enrichedTitle = resolved?.title || title;
                        const coverCandidate = resolved?.coverUrl || resolved?.albumCoverUrl || posterUrl;
                        const finalCover = (coverCandidate && !coverCandidate.startsWith('blob:') && !coverCandidate.startsWith('data:'))
                            ? normalizeFileUrl(coverCandidate)
                            : posterUrl;

                        navigator.mediaSession.metadata = new MediaMetadata({
                            title: enrichedTitle,
                            artist: enrichedArtist,
                            album: 'Spotip17an',
                            artwork: buildArtwork(finalCover)
                        });
                    } catch (enrichErr) {
                        console.warn("Could not enrich mediaSession metadata:", enrichErr);
                    }
                })();
            }
        } catch (mediaErr) {
            console.error("Error setting MediaSession:", mediaErr);
        }
    };

    function updateSongPlayerTime(currentTime: number, totalDuration: number, force?: boolean, forceSave?: boolean) {
        if (!timeSlider) return;
        if (!timeSlider.isSliding() || force) {
            timeSlider.setProgress(currentTime * 1000);
            setCurrentTime(parseTime(currentTime));

            // Guardar el tiempo actual en localStorage y bloquear el guardado 5 segundos
            if ((!saveTimeThrottle.current || forceSave) && !timeSlider.isSliding() && playingSong.current && (playingSong.current.id > 0 || playingSong.current.id.length > 4)) {
                LocalStorage.setItem(CURR_TIME_KEY, currentTime);
                if (saveTimeThrottle.current) {
                    clearTimeout(saveTimeThrottle.current);
                }
                saveTimeThrottle.current = setTimeout(() => {
                    saveTimeThrottle.current = null;
                }, 5000);
            }
        }
        timeSlider.setMax(totalDuration * 1000);
        setDuration(parseTime(totalDuration));
    }

    function goToTimeSongPlayer(time: number) {
        if (!audioPlayer) return;
        safeSetMediaSessionPositionState({
            duration: audioPlayer.duration || playingSong.current?.duration || 1,
            playbackRate: audioPlayer.playbackRate || 1,
            position: time / 1000
        });
        audioPlayer.currentTime = time / 1000;
        isPlayingIntent.current = true;
        audioPlayer.play();
        updateSongPlayerTime(audioPlayer.currentTime, audioPlayer.duration);
    }

    const transitionToNextTrack = useCallback((fromEndedEvent: boolean = false) => {
        if (isTransitioningTrack.current) return;
        isTransitioningTrack.current = true;

        setTimeout(() => {
            isTransitioningTrack.current = false;
        }, 1200);

        LocalStorage.setItem(CURR_TIME_KEY, 0);

        if (loopMode === 'song' && playingSong.current) {
            goToTimeSongPlayer(0);
            return;
        }

        let nextSong: Song | undefined = undefined;
        if (loopMode === 'queue' && !state.songQueue?.length && state.songHistory?.length) {
            const refreshedQueue = randomQueue
                ? [...state.songHistory.sort(() => Math.random() - 0.5)]
                : [...state.songHistory];
            setQueue(refreshedQueue);
            emptyHistory();
            nextSong = refreshedQueue[0];
        } else if (state.songQueue?.length) {
            nextSong = state.songQueue[0];
        }

        if (nextSong && audioPlayer) {
            isPlayingIntent.current = true;
            if ('mediaSession' in navigator) {
                navigator.mediaSession.playbackState = 'playing';
            }
            playSong(nextSong, true, 0);
            playNextSong();
        } else {
            isPlayingIntent.current = false;
            if ('mediaSession' in navigator) {
                navigator.mediaSession.playbackState = 'paused';
            }
        }
    }, [loopMode, state.songQueue, state.songHistory, randomQueue, audioPlayer, playNextSong, setQueue, emptyHistory]);

    function nextTrack() {
        isTransitioningTrack.current = false;
        transitionToNextTrack(false);
    }

    const playerControls = {
        stopTrack: (e?: React.MouseEvent) => {
            e?.stopPropagation();
            if (isBotMode && botGuildId) {
                DiscordBotApi.pause(botGuildId).catch(e => console.error("Error pausing bot:", e));
                return;
            }
            isPlayingIntent.current = false;
            if ('mediaSession' in navigator) {
                navigator.mediaSession.playbackState = 'paused';
            }
            audioPlayer?.pause();
        },
        playTrack: (e?: React.MouseEvent) => {
            e?.stopPropagation();
            if (isBotMode && botGuildId) {
                DiscordBotApi.resume(botGuildId).catch(e => console.error("Error resuming bot:", e));
                return;
            }
            isPlayingIntent.current = true;
            if ('mediaSession' in navigator) {
                navigator.mediaSession.playbackState = 'playing';
            }
            audioPlayer?.play();
        },
        prevTrack: (e?: React.MouseEvent) => {
            e?.stopPropagation();
            if (isBotMode && botGuildId) {
                toast.error("El bot de Discord no soporta canción anterior");
                return;
            }
            prevTrack();
        },
        nextTrack: (e?: React.MouseEvent) => {
            e?.stopPropagation();
            if (isBotMode && botGuildId) {
                DiscordBotApi.next(botGuildId).catch(e => console.error("Error skipping bot track:", e));
                return;
            }
            nextTrack();
        }
    };

    const prevTrack = () => {
        if (!audioPlayer) return;

        if (audioPlayer.currentTime > 5) {
            goToTimeSongPlayer(0);
        }
    };

    const audioPlayerMethods = {
        onError: () => {
            handleSongError();
        },
        onCanPlay: () => {
            if (audioPlayer && isPlayingIntent.current && audioPlayer.paused) {
                audioPlayer.play().catch(() => {});
            }
        },
        onLoadedMetadata: () => {
            if (!audioPlayer) return;
            if (!isNaN(audioPlayer.duration) && audioPlayer.duration > 0) {
                safeSetMediaSessionPositionState({
                    duration: audioPlayer.duration,
                    playbackRate: audioPlayer.playbackRate || 1,
                    position: audioPlayer.currentTime || 0
                });
                updateSongPlayerTime(audioPlayer.currentTime, audioPlayer.duration);
            }
        },
        onEnded: () => {
            if (!isTransitioningTrack.current) {
                transitionToNextTrack(true);
            }
        },
        onPlay: () => {
            isPlayingIntent.current = true;
            if ('mediaSession' in navigator) {
                navigator.mediaSession.playbackState = 'playing';
            }
            setPaused(false);
            if (!audioPlayer) return;
            const duration = (!isNaN(audioPlayer.duration) && audioPlayer.duration > 0)
                ? audioPlayer.duration
                : (playingSong.current?.duration || currentSong?.duration || 1);
            safeSetMediaSessionPositionState({
                duration: duration,
                playbackRate: audioPlayer.playbackRate || 1,
                position: audioPlayer.currentTime || 0
            });
            updateSongPlayerTime(audioPlayer.currentTime, duration);
        },
        onPause: () => {
            if ('mediaSession' in navigator) {
                if (!isPlayingIntent.current) {
                    navigator.mediaSession.playbackState = 'paused';
                } else {
                    // Internal pause during track transition/buffering: KEEP 'playing' to maintain OS notification!
                    navigator.mediaSession.playbackState = 'playing';
                }
            }
            if (!isPlayingIntent.current) {
                setPaused(true);
            }
            if (audioPlayer && !isNaN(audioPlayer.duration)) {
                safeSetMediaSessionPositionState({
                    duration: audioPlayer.duration,
                    playbackRate: audioPlayer.playbackRate,
                    position: audioPlayer.currentTime
                });
                if (!isPlayingIntent.current) {
                    updateSongPlayerTime(audioPlayer.currentTime, audioPlayer.duration, false, true);
                }
            }
        },
        onTimeUpdate: () => {
            if (!audioPlayer) return;
            // When screen is locked, onTimeUpdate is our active background pulse:
            // Proactively preload next song in queue when nearing the end of current track
            if (!isNaN(audioPlayer.duration) && audioPlayer.duration > 2) {
                const remainingTime = audioPlayer.duration - audioPlayer.currentTime;
                if (remainingTime <= 30) {
                    preloadNextTrackInBackground();
                }

                // Seamless automatic transition just before track ends (<= 0.35s):
                // Swapping source while the audio element is still actively playing ensures
                // Android OS maintains AudioFocus and DOES NOT kill the lockscreen MediaSession notification!
                const hasNext = (state.songQueue && state.songQueue.length > 0) ||
                                (loopMode === 'queue' && state.songHistory && state.songHistory.length > 0) ||
                                loopMode === 'song';

                if (remainingTime <= 0.35 && isPlayingIntent.current && !isTransitioningTrack.current && hasNext) {
                    transitionToNextTrack(false);
                }
            }

            if (!timeUpdateThrottle.current && !isNaN(audioPlayer.duration)) {
                if ('mediaSession' in navigator && (navigator.mediaSession.playbackState === 'playing' || isPlayingIntent.current)) {
                    const validDuration = audioPlayer.duration || currentSong?.duration || 0;
                    safeSetMediaSessionPositionState({
                        duration: validDuration,
                        playbackRate: audioPlayer.playbackRate || 1,
                        position: audioPlayer.currentTime || 0
                    });
                    updateSongPlayerTime(audioPlayer.currentTime, audioPlayer.duration);
                    timeUpdateThrottle.current = setTimeout(() => {
                        timeUpdateThrottle.current = null;
                    }, 1000);
                }
            }
        }
    };

    return { duration, currentTime, paused, prevTrack, playerControls, audioPlayerMethods, updateSongPlayerTime };
}

export default useAudioPlayer;
